import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { transfersAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Package, Truck, CheckCircle, AlertTriangle, Clock,
  Eye, PackageCheck, RefreshCw, Building2, FileText
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const statusConfig = {
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', icon: Package },
  packed: { label: 'Packed', color: 'bg-blue-100 text-blue-700', icon: PackageCheck },
  dispatched: { label: 'Dispatched', color: 'bg-purple-100 text-purple-700', icon: Truck },
  in_transit: { label: 'In Transit', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  discrepancy: { label: 'Discrepancy', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle }
};

const StoreIncomingTransfers = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const storeId = user?.entity_id;
  
  // Main state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialogs
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Receive form
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveNotes, setReceiveNotes] = useState('');

  // Fetch incoming transfer orders
  const fetchOrders = useCallback(async () => {
    if (!storeId) return;
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: 20,
        destination_id: storeId
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await transfersAPI.listOrders(params);
      setOrders(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({ title: 'Error', description: 'Failed to fetch incoming transfers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [storeId, page, statusFilter, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Get status badge
  const getStatusBadge = (status) => {
    const config = statusConfig[status];
    if (!config) return <Badge variant="outline">{status}</Badge>;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" /> {config.label}
      </Badge>
    );
  };

  // View order details
  const viewOrderDetails = async (order) => {
    try {
      const response = await transfersAPI.getOrder(order.id);
      setSelectedOrder(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch order details', variant: 'destructive' });
    }
  };

  // Open receive dialog
  const openReceiveDialog = async (order) => {
    try {
      const response = await transfersAPI.getOrder(order.id);
      const orderData = response.data;
      
      setSelectedOrder(orderData);
      setReceiveItems(orderData.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku,
        dispatched_qty: item.dispatched_qty || item.approved_qty,
        received_qty: item.dispatched_qty || item.approved_qty,
        accepted_qty: item.dispatched_qty || item.approved_qty,
        damaged_qty: 0,
        receiver_notes: ''
      })));
      setReceiveNotes('');
      setShowReceiveDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load order details', variant: 'destructive' });
    }
  };

  // Update received quantity
  const updateReceivedQty = (productId, qty) => {
    setReceiveItems(items => items.map(item => {
      if (item.product_id === productId) {
        const receivedQty = Math.max(0, parseFloat(qty) || 0);
        return { 
          ...item, 
          received_qty: receivedQty,
          accepted_qty: receivedQty - item.damaged_qty
        };
      }
      return item;
    }));
  };

  // Update damaged quantity
  const updateDamagedQty = (productId, qty) => {
    setReceiveItems(items => items.map(item => {
      if (item.product_id === productId) {
        const damagedQty = Math.max(0, Math.min(parseFloat(qty) || 0, item.received_qty));
        return { 
          ...item, 
          damaged_qty: damagedQty,
          accepted_qty: item.received_qty - damagedQty
        };
      }
      return item;
    }));
  };

  // Update receiver notes
  const updateReceiverNotes = (productId, notes) => {
    setReceiveItems(items => items.map(item => 
      item.product_id === productId ? { ...item, receiver_notes: notes } : item
    ));
  };

  // Check for discrepancies
  const hasDiscrepancy = receiveItems.some(item => 
    item.received_qty !== item.dispatched_qty || item.damaged_qty > 0
  );

  // Handle receive
  const handleReceive = async () => {
    if (!selectedOrder) return;

    setSubmitting(true);
    try {
      await transfersAPI.receiveOrder(selectedOrder.id, {
        items: receiveItems.map(item => ({
          product_id: item.product_id,
          received_qty: item.received_qty,
          accepted_qty: item.accepted_qty,
          damaged_qty: item.damaged_qty,
          receiver_notes: item.receiver_notes
        })),
        receipt_notes: receiveNotes
      });
      
      toast({ 
        title: hasDiscrepancy ? 'Received with Discrepancy' : 'Received Successfully',
        description: hasDiscrepancy 
          ? 'Stock received and discrepancies have been logged' 
          : 'Stock has been added to your inventory'
      });
      
      setShowReceiveDialog(false);
      fetchOrders();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to receive stock', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Stats
  const inTransitCount = orders.filter(o => ['dispatched', 'in_transit'].includes(o.status)).length;
  const completedCount = orders.filter(o => o.status === 'completed').length;
  const discrepancyCount = orders.filter(o => o.status === 'discrepancy').length;

  return (
    <div className="p-6 space-y-6" data-testid="store-incoming-transfers-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-emerald-600" />
            Incoming Stock
          </h1>
          <p className="text-gray-500">Receive incoming stock transfers from warehouse</p>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">In Transit</p>
                <p className="text-2xl font-bold text-purple-800">{inTransitCount}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Completed</p>
                <p className="text-2xl font-bold text-emerald-800">{completedCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">With Discrepancy</p>
                <p className="text-2xl font-bold text-orange-800">{discrepancyCount}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Orders</p>
                <p className="text-2xl font-bold text-blue-800">{orders.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-gray-600">Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="discrepancy">With Discrepancy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Transfer #</TableHead>
                <TableHead>From</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    Loading transfers...
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No incoming transfers found</p>
                    <p className="text-sm">Submit stock requests to HQ to receive transfers</p>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-gray-50">
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600 font-medium">
                        {order.transfer_number}
                      </span>
                      {order.stock_request_number && (
                        <p className="text-xs text-gray-500">
                          Req: {order.stock_request_number}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-teal-500" />
                        <div>
                          <p className="font-medium text-gray-800">{order.source_name}</p>
                          <p className="text-xs text-gray-500">{order.source_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {order.items?.length || 0}
                    </TableCell>
                    <TableCell className="text-right font-medium text-gray-800">
                      ₹{(order.grand_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewOrderDetails(order)}
                          className="text-gray-600 hover:text-blue-600"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {['dispatched', 'in_transit'].includes(order.status) && (
                          <Button 
                            size="sm"
                            onClick={() => openReceiveDialog(order)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                          >
                            <PackageCheck className="w-4 h-4" /> Receive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Package className="w-5 h-5 text-blue-600" />
              Transfer Details - {selectedOrder?.transfer_number}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Status & Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Source</Label>
                  <p className="font-medium text-gray-800 mt-1">{selectedOrder.source_name}</p>
                </div>
              </div>

              {/* Dispatch Info */}
              {selectedOrder.dispatch_info?.vehicle_number && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-xs text-blue-600">Dispatch Details</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>
                      <p className="text-gray-500">Vehicle</p>
                      <p className="font-medium">{selectedOrder.dispatch_info.vehicle_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Driver</p>
                      <p className="font-medium">{selectedOrder.dispatch_info.driver_name}</p>
                      {selectedOrder.dispatch_info.driver_mobile && (
                        <p className="text-gray-500">{selectedOrder.dispatch_info.driver_mobile}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <Label className="text-gray-700 font-medium">Items</Label>
                <div className="border rounded-lg overflow-hidden mt-2">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Dispatched</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            {item.dispatched_qty || item.approved_qty}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.received_qty || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            ₹{(item.transfer_price || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-right">
                  <span className="font-bold">Total: ₹{(selectedOrder.grand_total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <PackageCheck className="w-5 h-5 text-emerald-600" />
              Receive Stock - {selectedOrder?.transfer_number}
            </DialogTitle>
            <DialogDescription>
              From: {selectedOrder?.source_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Items */}
            <div>
              <Label className="text-gray-700 font-medium mb-2 block">Verify Received Items</Label>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right w-24">Dispatched</TableHead>
                      <TableHead className="text-right w-28">Received</TableHead>
                      <TableHead className="text-right w-28">Damaged</TableHead>
                      <TableHead className="w-40">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receiveItems.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {item.dispatched_qty}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input 
                            type="number"
                            min="0"
                            max={item.dispatched_qty}
                            value={item.received_qty}
                            onChange={(e) => updateReceivedQty(item.product_id, e.target.value)}
                            className="w-20 text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input 
                            type="number"
                            min="0"
                            max={item.received_qty}
                            value={item.damaged_qty}
                            onChange={(e) => updateDamagedQty(item.product_id, e.target.value)}
                            className="w-20 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            placeholder="Notes..."
                            value={item.receiver_notes}
                            onChange={(e) => updateReceiverNotes(item.product_id, e.target.value)}
                            className="text-sm"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Discrepancy Warning */}
            {hasDiscrepancy && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Discrepancy Detected</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Some items have quantity differences or damage. This will be logged for investigation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Receipt Notes */}
            <div className="space-y-2">
              <Label className="text-gray-600">Receipt Notes (Optional)</Label>
              <Textarea 
                placeholder="Add any notes about this receipt..."
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                rows={2}
              />
            </div>

            {/* Summary */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-500">Total Received</p>
                  <p className="text-lg font-bold text-gray-800">
                    {receiveItems.reduce((sum, i) => sum + i.received_qty, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Accepted</p>
                  <p className="text-lg font-bold text-emerald-600">
                    {receiveItems.reduce((sum, i) => sum + i.accepted_qty, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Damaged</p>
                  <p className="text-lg font-bold text-red-600">
                    {receiveItems.reduce((sum, i) => sum + i.damaged_qty, 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleReceive}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <PackageCheck className="w-4 h-4 mr-2" />
              {submitting ? 'Processing...' : 'Confirm Receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreIncomingTransfers;
